import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StateManager } from '../managers/StateManager';
import { HarnessBackground } from '../types';
import { COLORS } from '../utils/constants';
import { downscaleToWebp } from '../utils/image';
import {
  BACKGROUND_MAX_EDGE,
  BACKGROUND_WEBP_QUALITY,
  calibrateScale,
  createBackground,
  WorldPoint
} from '../utils/background';
import { logger } from '../utils/logger';
import {
  panelStyle,
  labelStyle,
  hintStyle,
  inputStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  iconButtonStyle,
  toggleButtonStyle
} from './BackgroundControls.styles';

interface BackgroundControlsProps {
  stateManager: StateManager;
  /** Converts client (page) coordinates to world coordinates, or null if not ready. */
  screenToWorld: (clientX: number, clientY: number) => { x: number; y: number } | null;
  visible: boolean;
  onClose: () => void;
}

type Mode = 'idle' | 'reposition' | 'calibrate';

/** A calibration point with both its screen (for drawing) and world (for math) position. */
interface CalibrationPoint {
  client: { x: number; y: number };
  world: WorldPoint;
}

interface DragState {
  startWorld: WorldPoint;
  startX: number;
  startY: number;
}

/**
 * Floating panel to add and place a reference image behind the diagram. Scaling
 * is done by two-point calibration against a feature of known real length, and
 * positioning by dragging the image on the canvas.
 */
export const BackgroundControls: React.FC<BackgroundControlsProps> = ({
  stateManager,
  screenToWorld,
  visible,
  onClose
}) => {
  const [background, setBackground] = useState<HarnessBackground | undefined>(
    stateManager.getState().background
  );
  const [mode, setMode] = useState<Mode>('idle');
  const [calibA, setCalibA] = useState<CalibrationPoint | null>(null);
  const [calibB, setCalibB] = useState<CalibrationPoint | null>(null);
  const [mmInput, setMmInput] = useState('');
  const [fillableCount, setFillableCount] = useState(0);
  const dragState = useRef<DragState | null>(null);

  useEffect(() => {
    // Re-sync on attach: saved data may have loaded before this subscription ran.
    const sync = () => {
      setBackground(stateManager.getState().background);
      setFillableCount(stateManager.getFillableSegmentCount());
    };
    sync();
    return stateManager.subscribe(sync);
  }, [stateManager]);

  const resetCalibration = useCallback(() => {
    setCalibA(null);
    setCalibB(null);
    setMmInput('');
  }, []);

  // Leaving the panel or losing the image ends any in-progress placement gesture.
  useEffect(() => {
    if (!visible || !background) {
      setMode('idle');
      resetCalibration();
      if (dragState.current) {
        dragState.current = null;
        stateManager.endBatch();
      }
    }
  }, [visible, background, resetCalibration, stateManager]);

  const pickImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }
      try {
        const raster = await downscaleToWebp(file, BACKGROUND_MAX_EDGE, BACKGROUND_WEBP_QUALITY);
        stateManager.setBackground(createBackground(raster.src, raster.width, raster.height));
      } catch (error) {
        logger.storage.error('Failed to import background image', error);
        alert('Could not load that image: ' + (error as Error).message);
      }
    };
    input.click();
  }, [stateManager]);

  const handleRepositionDown = useCallback((event: React.PointerEvent) => {
    if (!background || background.locked) {
      return;
    }
    const world = screenToWorld(event.clientX, event.clientY);
    if (!world) {
      return;
    }
    dragState.current = { startWorld: world, startX: background.x, startY: background.y };
    stateManager.beginBatch();
    (event.target as Element).setPointerCapture(event.pointerId);
  }, [background, screenToWorld, stateManager]);

  const handleRepositionMove = useCallback((event: React.PointerEvent) => {
    const drag = dragState.current;
    if (!drag) {
      return;
    }
    const world = screenToWorld(event.clientX, event.clientY);
    if (!world) {
      return;
    }
    stateManager.updateBackground({
      x: drag.startX + (world.x - drag.startWorld.x),
      y: drag.startY + (world.y - drag.startWorld.y)
    });
  }, [screenToWorld, stateManager]);

  const handleRepositionUp = useCallback(() => {
    if (dragState.current) {
      dragState.current = null;
      stateManager.endBatch();
    }
  }, [stateManager]);

  const handleCalibrateClick = useCallback((event: React.MouseEvent) => {
    const world = screenToWorld(event.clientX, event.clientY);
    if (!world) {
      return;
    }
    const point: CalibrationPoint = { client: { x: event.clientX, y: event.clientY }, world };
    if (!calibA) {
      setCalibA(point);
    } else if (!calibB) {
      setCalibB(point);
    }
  }, [screenToWorld, calibA, calibB]);

  const applyCalibration = useCallback(() => {
    const realMm = parseFloat(mmInput);
    if (!background || !calibA || !calibB || !isFinite(realMm) || realMm <= 0) {
      return;
    }
    const next = calibrateScale(background, calibA.world, calibB.world, realMm);
    stateManager.updateBackground(next);
    resetCalibration();
    setMode('idle');
  }, [mmInput, background, calibA, calibB, stateManager, resetCalibration]);

  if (!visible) {
    return null;
  }

  const overlayActive = background && mode !== 'idle';

  return (
    <>
      {overlayActive && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 450,
            cursor: mode === 'reposition' ? 'move' : 'crosshair'
          }}
          onPointerDown={mode === 'reposition' ? handleRepositionDown : undefined}
          onPointerMove={mode === 'reposition' ? handleRepositionMove : undefined}
          onPointerUp={mode === 'reposition' ? handleRepositionUp : undefined}
          onClick={mode === 'calibrate' ? handleCalibrateClick : undefined}
        />
      )}

      {mode === 'calibrate' && <CalibrationGuides a={calibA} b={calibB} />}

      <div style={panelStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <strong style={{ fontSize: '14px', color: '#2d3436' }}>Background image</strong>
          <button onClick={onClose} style={iconButtonStyle} title="Close">×</button>
        </div>

        {!background && (
          <>
            <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#636e72' }}>
              Place a floor plan or layout behind the diagram to position connectors against it.
            </p>
            <button onClick={pickImage} style={primaryButtonStyle}>Add image…</button>
          </>
        )}

        {background && (
          <>
            <label style={labelStyle}>
              Opacity
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(background.opacity * 100)}
                onChange={(e) => stateManager.updateBackground({ opacity: Number(e.target.value) / 100 })}
                style={{ width: '100%' }}
              />
            </label>

            <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={background.locked}
                onChange={(e) => stateManager.updateBackground({ locked: e.target.checked })}
              />
              Lock position
            </label>

            <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={background.showInReport ?? false}
                onChange={(e) => stateManager.updateBackground({ showInReport: e.target.checked })}
              />
              Show in printed report
            </label>

            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <button
                onClick={() => setMode(mode === 'reposition' ? 'idle' : 'reposition')}
                disabled={background.locked}
                style={toggleButtonStyle(mode === 'reposition', background.locked)}
              >
                {mode === 'reposition' ? 'Done moving' : 'Move'}
              </button>
              <button
                onClick={() => { resetCalibration(); setMode(mode === 'calibrate' ? 'idle' : 'calibrate'); }}
                style={toggleButtonStyle(mode === 'calibrate', false)}
              >
                {mode === 'calibrate' ? 'Cancel' : 'Calibrate scale'}
              </button>
            </div>

            {mode === 'calibrate' && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#636e72' }}>
                {!calibA && <p style={hintStyle}>Click the first end of a feature of known length.</p>}
                {calibA && !calibB && <p style={hintStyle}>Click the second end.</p>}
                {calibA && calibB && (
                  <div>
                    <label style={labelStyle}>
                      Real length between points (mm)
                      <input
                        type="number"
                        min={0}
                        autoFocus
                        value={mmInput}
                        onChange={(e) => setMmInput(e.target.value)}
                        style={inputStyle}
                      />
                    </label>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                      <button onClick={applyCalibration} style={primaryButtonStyle}>Apply</button>
                      <button onClick={resetCalibration} style={secondaryButtonStyle}>Redo points</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {fillableCount > 0 && (
              <button
                onClick={() => stateManager.fillMissingSegmentLengthsFromLayout()}
                title="Set a length for every section that has none yet, using the distance measured on the layout"
                style={{ ...secondaryButtonStyle, width: '100%', marginTop: '10px', borderColor: COLORS.PRIMARY, color: COLORS.HEADER }}
              >
                Fill {fillableCount} missing length{fillableCount === 1 ? '' : 's'} from layout
              </button>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button onClick={pickImage} style={secondaryButtonStyle}>Replace…</button>
              <button
                onClick={() => { stateManager.removeBackground(); }}
                style={{ ...secondaryButtonStyle, color: COLORS.DANGER, borderColor: COLORS.DANGER }}
              >
                Remove
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

/** Fixed-position markers and connecting line drawn over the two calibration clicks. */
const CalibrationGuides: React.FC<{ a: CalibrationPoint | null; b: CalibrationPoint | null }> = ({ a, b }) => (
  <svg style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 460 }}>
    {a && b && (
      <line x1={a.client.x} y1={a.client.y} x2={b.client.x} y2={b.client.y} stroke={COLORS.PRIMARY} strokeWidth={2} />
    )}
    {[a, b].map((point, index) => point && (
      <circle key={index} cx={point.client.x} cy={point.client.y} r={5} fill={COLORS.PRIMARY} stroke="white" strokeWidth={2} />
    ))}
  </svg>
);
